// Regional options approved in the prototype (Setări → Regional). Extending them is a product decision.

export const LANGS = ["ro", "en", "de", "fr", "it", "es", "hu"] as const;
export type Lang = (typeof LANGS)[number];

export const CURRENCIES = ["RON", "EUR"] as const;
export type Currency = (typeof CURRENCIES)[number];

export const COUNTRIES = [
  ["RO", "România"],
  ["MD", "Republica Moldova"],
  ["BG", "Bulgaria"],
  ["HU", "Ungaria"],
  ["AT", "Austria"],
  ["DE", "Germania"],
  ["IT", "Italia"],
] as const;
export type Country = (typeof COUNTRIES)[number][0];
export const COUNTRY_CODES = COUNTRIES.map(([code]) => code) as [Country, ...Country[]];

export const TIMEZONES = [
  ["Europe/Bucharest", "București (EET/EEST)"],
  ["Europe/Chisinau", "Chișinău"],
  ["Europe/Sofia", "Sofia"],
  ["Europe/Budapest", "Budapesta (CET)"],
  ["Europe/Vienna", "Viena (CET)"],
  ["Europe/Berlin", "Berlin (CET)"],
  ["Europe/Rome", "Roma (CET)"],
] as const;
export type Timezone = (typeof TIMEZONES)[number][0];
export const TIMEZONE_IDS = TIMEZONES.map(([id]) => id) as [Timezone, ...Timezone[]];

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 200;

export const ROLES = ["owner", "manager", "staff"] as const;
export type Role = (typeof ROLES)[number];

/** The 14 EU allergen groups (Reg. 1169/2011 Annex II), labels as in the approved prototype. */
export const ALLERGENS = [
  ["gluten", "Gluten (grâu, secară, orz, ovăz)", "Gluten"],
  ["crustacee", "Crustacee", "Crustaceans"],
  ["oua", "Ouă", "Eggs"],
  ["peste", "Pește", "Fish"],
  ["arahide", "Arahide", "Peanuts"],
  ["soia", "Soia", "Soy"],
  ["lapte", "Lapte (inclusiv lactoză)", "Milk"],
  ["nuci", "Fructe cu coajă lemnoasă", "Tree nuts"],
  ["telina", "Țelină", "Celery"],
  ["mustar", "Muștar", "Mustard"],
  ["susan", "Susan", "Sesame"],
  ["sulfiti", "Dioxid de sulf și sulfiți", "Sulphites"],
  ["lupin", "Lupin", "Lupin"],
  ["moluste", "Moluște", "Molluscs"],
] as const;
export type Allergen = (typeof ALLERGENS)[number][0];
export const ALLERGEN_CODES = ALLERGENS.map(([code]) => code) as [Allergen, ...Allergen[]];

export const PRODUCT_LABELS = [
  ["veg", "Vegetarian", "Vegetarian"],
  ["vegan", "Vegan", "Vegan"],
  ["picant", "Picant", "Spicy"],
  ["nou", "Nou", "New"],
  // The restaurant's pick (owner 2026-11-03, the badge competitors show as "Popular" / "Chef's choice").
  ["recomandat", "Recomandat", "Recommended"],
] as const;
export type ProductLabel = (typeof PRODUCT_LABELS)[number][0];
export const PRODUCT_LABEL_CODES = PRODUCT_LABELS.map(([code]) => code) as [ProductLabel, ...ProductLabel[]];

/** Languages a menu can be translated into (source is the restaurant's source language, Romanian for the pilot). */
/** Translation languages offered (owner, 2026-11-02, DEC-107): the menu language + 1 free, each more paid. */
export const TRANSLATION_LANGS = ["en", "de", "fr", "it", "es", "hu"] as const;
export type TranslationLang = (typeof TRANSLATION_LANGS)[number];

/** Each translation language as the restaurant reads it (Romanian, feminine: „meniul în germană”). */
export const TRANSLATION_LANG_NAMES: Record<TranslationLang, string> = {
  en: "engleză",
  de: "germană",
  fr: "franceză",
  it: "italiană",
  es: "spaniolă",
  hu: "maghiară",
};

/** Abuse/size guards. Generous for real menus, bounded so a published snapshot stays far below D1's row limit. */
export const LIMITS = {
  menusPerRestaurant: 20,
  categoriesPerMenu: 100,
  productsPerMenu: 500,
  variantsPerProduct: 10,
  /** Prototype editor: "JPG, PNG sau WebP, până la 8 MB". The browser downsizes before upload. */
  photoBytes: 8 * 1024 * 1024,
  photoMaxSide: 10_000,
  /** Technical guard against runaway uploads; unused photos are removed by the daily cleanup. */
  photosPerRestaurant: 5_000,
  /** Spec §5.4: "limită inițială per fișier 20 MB (configurabilă)". */
  importFileBytes: 20 * 1024 * 1024,
  /** Uploaded units of one import: pages prepared in the browser (a PDF counts with each page) or raw files. */
  importFilesPerJob: 30,
} as const;
